<script setup lang="ts">
import { omit } from 'radash'

const {
  d,
  locale: current_locale,
  locales,
  n,
  t,
} = useI18n({
  useScope: 'local',
})
const switchLocalePath = useSwitchLocalePath()
const localePath = useLocalePath()
const booking = useBookingStore()
const route = useRoute()

definePageMeta({
  auth: false,
  title: 'Home',
})

const { request } = useDirectusRest()
const dayjs = useDayjs()

const isHomePage = computed(() => route.path == localePath({ name: 'index' }))

const { currentStep, handleSubmit, data } = createMultiStepForm({
  initialState: {
    VAT_number: '',
    amount: null,
    bob_by_feestfiets: false,
    client_accepted_marketing: false,
    client_city: '',
    client_email: '',
    client_first_name: '',
    client_house_number: '',
    client_last_name: '',
    client_phone_number: '',
    client_postal_code: '',
    client_second_phone_number: '',
    client_street: '',
    collection_point: undefined,
    delivery_point: undefined,
    duration: undefined,
    hour: 10,
    minutes: 0,
    organization_name: '',
    package: undefined,
    start: undefined,
  },
  onSubmit: async (data, context) => {
    const booking = omit(
      {
        ...data,
        coupon: data.coupon?.code,
        delivery_fee: data.delivery_fee?.id,
        drink: data.drink.id,
        language: current_locale.value,
        package: data.package.id,
        start: data.startString,
        remarks: data.remarks,
      },
      ['startString', 'hour', 'minutes', 'pricing', 'slot'],
    )

    const response = await request(() => ({
      body: JSON.stringify(booking),
      method: 'POST',
      path: '/api/booking',
    }))

    if (response.paymentUrl) {
      return navigateTo(response.paymentUrl, { external: true })
    }
  },
  steps: [
    {
      id: 'package',
      meta: {},
      to: localePath({ name: 'index' }),
    },
    {
      id: 'time',
      meta: {
        title: t('when'),
      },
      to: localePath({ name: 'index-info' }),
    },
    {
      condition: (chosenPackage) => !chosenPackage?.location,
      id: 'location',
      meta: {
        title: t('where'),
      },
      to: localePath({ name: 'index-location' }),
    },
    {
      condition: (chosenPackage) =>
        !chosenPackage?.available_drinks?.length ||
        chosenPackage?.available_drinks?.length > 1,
      id: 'drinks',
      meta: {
        title: t('drinks'),
      },
      to: localePath({ name: 'index-drink' }),
    },
    {
      id: 'info',
      meta: {
        title: t('personal_info'),
      },
      to: localePath({ name: 'index-contact' }),
    },
    {
      id: 'overview',
      meta: {
        title: t('overview'),
      },
      to: localePath({ name: 'index-overview' }),
    },
    {
      id: 'end',
      meta: {
        title: t('done'),
      },
      to: localePath({ name: 'index-end' }),
    },
  ],
})

let stepTitles = [
  t('when'),
  t('where'),
  t('drinks'),
  t('personal_info'),
  t('done'),
]

// const { data: content } = await useAsyncData(
//   () =>
//     request(
//       readSingleton('content', {
//         deep: {
//           translations: {
//             _filter: {
//               languages_code: {
//                 _eq: current_locale.value,
//               },
//             },
//           },
//         },
//         fields: ['id', { translations: ['features'] }],
//       }),
//     ),
//   {
//     transform: (result) => {
//       return result.translations[0]
//     },
//   },
// )
</script>

<template>
  <div>
    <NuxtPage />
  </div>
</template>

<style lang="css">
.step-shadow {
  box-shadow: 4px 10px 20px rgba(0, 0, 0, 0.18);
}

@media screen and (min-width: 768px) {
  .marker::after {
    content: '';
    display: block;
    position: absolute;
    width: 105%;
    height: 100%;
    left: 50%;
    top: 0px;
    z-index: 0;
    transform: translateX(-50%);
    background-image: url('/img/marker.svg');
    mix-blend-mode: multiply;
    background-blend-mode: multiply;
    background-repeat: no-repeat;
    background-position: 50% 100%;
    background-size: 100%;
  }
}

.marker-alt::after {
  content: '';
  display: block;
  position: absolute;
  width: 100%;
  height: 100%;
  left: 50%;
  top: 0px;
  transform: translateX(-50%);
  mix-blend-mode: multiply;
  background-blend-mode: multiply;
  background-image: url('/img/marker2.svg');
  background-repeat: no-repeat;
  background-position: 50% 100%;
  background-size: 100%;
}

@media screen and (min-width: 450px) {
  .marker-alt::after {
    content: '';
    display: block;
    position: absolute;
    width: 110%;
    height: 100%;
    left: 50%;
    top: 0px;
    transform: translateX(-50%);
    mix-blend-mode: multiply;
    background-blend-mode: multiply;
    background-image: url('/img/marker2.svg');
    background-repeat: no-repeat;
    background-position: 50% 100%;
    background-size: 100%;
  }
}
</style>

<i18n lang="json">{
  "nl": {
    "about": "over ons",
    "questions": "vragen",
    "when": "wanneer",
    "next_step": "volgende stap",
    "where": "waar",
    "drinks": "drank",
    "personal_info": "persoonlijke info",
    "done": "Klaar !",
    "hero_title": "De leukste bierfiets van het land!",
    "hero_subtitle": "Boek op onze vaste locaties of een door jou gekozen locatie.",
    "terms_of_service": "Algemene voorwaarden",
    "drivers_delaration": "Bestuurdersverklaring",
    "frequently_asked_questions": "Veelgestelde vragen"
  },
  "en": {
    "about": "about us",
    "questions": "questions",
    "when": "when",
    "next_step": "Next step",
    "where": "where",
    "drinks": "drinks",
    "personal_info": "personal info",
    "done": "Done !",
    "hero_title": "The best beer bike in the country!",
    "hero_subtitle": "Book at our fixed locations or a location of your choice.",
    "terms_of_service": "Terms of service",
    "drivers_delaration": "Drivers declaration",
    "frequently_asked_questions": "Frequently asked questions"
  },
  "fr": {
    "about": "à propos",
    "questions": "questions",
    "when": "quand",
    "next_step": "étape suivante",
    "where": "où",
    "drinks": "boissons",
    "personal_info": "informations personnelles",
    "done": "Terminé !",
    "hero_title": "Le meilleur vélo à bière du pays !",
    "hero_subtitle": "Réservez à nos emplacements fixes ou à l'emplacement de votre choix.",
    "terms_of_service": "Conditions d'utilisation",
    "drivers_delaration": "Déclaration des conducteurs",
    "frequently_asked_questions": "Questions fréquemment posées"
  }
}</i18n>
